import { PaymentMethodType } from '../../services/PaymentManager.types'
import { normalizeAddress } from '../addressStore/address.utils'
import { mixtilesAxios } from '../../utils/ApiUtils'
import { translateManager as t } from '../../services/TranslateManager'
import { getStripeAmountToCharge } from '../../utils/utils'
import { logger } from '../../services/logger'

export function isPaymentMethodGoogleOrApple(
  paymentMethodType: PaymentMethodType
) {
  return [PaymentMethodType.GOOGLE_PAY, PaymentMethodType.APPLE_PAY].includes(
    paymentMethodType
  )
}

// Google/Apple Pay flows write a paymentMethodId only after the user completes
// the Stripe payment sheet. A cached googlePay/applePay type without an id
// means the user has a stale selection from a session where the sheet never
// ran — submitting the order would ship paymentMethodId: null to Stripe.
export function isStaleStripePaymentMethod(
  paymentMethodType: PaymentMethodType | null | undefined,
  paymentMethodId: string | null | undefined
): boolean {
  return (
    !!paymentMethodType &&
    isPaymentMethodGoogleOrApple(paymentMethodType) &&
    !paymentMethodId
  )
}

// When validation runs as a gate for the native Apple/Google Pay button click,
// a null paymentMethodId is the expected state — the click itself is what
// triggers the Stripe payment sheet that populates the id. Treating it as
// stale here would block the user from ever reaching the native sheet.
export function shouldTreatAsStalePaymentMethod({
  paymentMethodType,
  paymentMethodId,
  isFromPaymentRequestButton,
}: {
  paymentMethodType: PaymentMethodType | null | undefined
  paymentMethodId: string | null | undefined
  isFromPaymentRequestButton: boolean
}): boolean {
  if (isFromPaymentRequestButton) {
    return false
  }
  return isStaleStripePaymentMethod(paymentMethodType, paymentMethodId)
}

export function shouldRoundForPaypal(currency: string, value: number) {
  if (currency === 'JPY') {
    return Math.round(value)
  } else {
    return parseFloat(value.toFixed(2))
  }
}

export async function initiatePaypalPayment(
  orderPriceSummary: any,
  address: any,
  paypalAccountName: string
) {
  const shippingTotal = shouldRoundForPaypal(
    orderPriceSummary.currency,
    orderPriceSummary.shippingCost || 0
  )
  const taxTotal = shouldRoundForPaypal(
    orderPriceSummary.currency,
    orderPriceSummary.tax
  )
  const amount = shouldRoundForPaypal(
    orderPriceSummary.currency,
    orderPriceSummary.totalPrice
  )
  const itemTotal = amount - shippingTotal - taxTotal

  const params = {
    order_size: orderPriceSummary.totalItems,
    shipping_total: shippingTotal,
    tax_total: taxTotal,
    amount,
    item_total: itemTotal,
    currency_code: orderPriceSummary.currency,
    address: normalizeAddress(address, false),
    paypalAccountName,
    product_type: orderPriceSummary.productType || 'mixtiles',
  }
  const { paypal_transaction_id: transactionId } = await mixtilesAxios
    .post('v1/paypal/payment', params)
    .then((response) => response.data)

  return transactionId
}

// Returns whether the payment method can be saved for future use: https://stripe.com/docs/payments/payment-methods#usage
// For example some BNPL payment methods cannot be attached to Customers.
export function isReusablePaymentMethod(paymentMethodType: PaymentMethodType) {
  return [
    PaymentMethodType.CREDIT_CARD,
    PaymentMethodType.PAYPAL,
    PaymentMethodType.APPLE_PAY,
    PaymentMethodType.GOOGLE_PAY,
    PaymentMethodType.LINK,
  ].includes(paymentMethodType)
}

export function updatePaymentRequest({
  paymentRequest,
  orderPriceSummary,
  displayItems,
}: {
  paymentRequest: any
  orderPriceSummary: any
  displayItems: any
}) {
  const totalLabel = t.get('order.checkout.payment_summary_rows.total')
  paymentRequest.update({
    currency: orderPriceSummary.currency.toLowerCase(),
    total: {
      label: totalLabel,
      amount: getStripeAmountToCharge({
        totalCost: orderPriceSummary.totalPrice,
        currency: orderPriceSummary.currency,
      }),
    },
    displayItems,
  })
}

// Look later
export function getPayPalKey(paypalAccountName: string) {
  switch (paypalAccountName) {
    case 'US':
      return window.KEYS.paypalUSClientId
    case 'US_2':
      return window.KEYS.paypalUS2ClientId
    case 'NL':
      return window.KEYS.paypalNLClientId
    default:
      logger.warning('Missing paypal key for given paypal account name', {
        paypalAccountName,
      })
      return window.KEYS.paypalUSClientId
  }
}
